import { Box, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Header, Row } from '../../components/Table/TableModels';
import useHttpError from '../../hooks/useHttpError/useHttpError';
import usePortalConfig from '../../hooks/usePortalConfig/usePortalConfig';
import useRolePermissions from '../../hooks/useRolePermissions/useRolePermissions';
import useSnackBar from '../../hooks/useSnackBar/useSnackBar';
import { Admin, AdminsListRequest, AllowedAdminOperations } from '../../models/AdminOnboardingModels';
import { Sort } from '../../models/CommonModels';
import AdminOnboardingService from '../../services/AdminOnboarding/AdminOnboardingService';
import { AdminFilter } from './AdminsTableFilters';
import ClickableIcon from '../../components/Icons/ClickableIcon';
import PencilIcon from '../../components/Icons/PencilIcon';
import AdminRowOptions from './components/AdminRowOptions';

function useAdminsTable(activePage: number, pageSize: number, filter: AdminFilter, sort: Sort) {
  const [t] = useTranslation();
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const snackBar = useSnackBar();
  const { getHttpReason } = useHttpError();
  const { isUserAllowed } = useRolePermissions();
  const { getRoleTranslation } = usePortalConfig();
  const { routePermissions } = usePortalConfig();
  const theme = useTheme();

  /**
   * Fetches the list of admins
   */
  const fetchAdmins = () => {
    const requestParams: AdminsListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      ...filter,
    };
    AdminOnboardingService.api.fetchAdmins(requestParams).then(
      (response) => {
        setAdmins(response.data.administrators);
        setTotalAdmins(response.data.totalElements);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.adminOnboarding.fetchAdminsError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Delete admin
   * @param id
   */
  const deleteAdmin = (id: string) => {
    AdminOnboardingService.api.deleteAdmin(id).then(
      () => {
        fetchAdmins();
      },
      (res) => {
        const error = getHttpReason(res, t('pages.adminOnboarding.deleteAdminsError'));
        snackBar.showSnackBar(error, 'error');
        setIsLoading(false);
      },
    );
  };

  React.useEffect(() => {
    setIsLoading(true);
    fetchAdmins();
  }, [activePage, pageSize, filter, sort]);

  const headers: Header[] = useMemo(
    () => [
      {
        id: 'lastName',
        label: t('pages.adminOnboarding.table.headers.lastName'),
        isSortable: true,
      },

      {
        id: 'firstName',
        label: t('pages.adminOnboarding.table.headers.firstName'),
        isSortable: true,
      },
      {
        id: 'email',
        label: t('pages.adminOnboarding.table.headers.emailAddress'),
        isSortable: true,
      },
      {
        id: 'role',
        label: t('pages.adminOnboarding.table.headers.role'),
        isSortable: true,
      },
      {
        id: 'company',
        label: t('pages.adminOnboarding.table.headers.company'),
        isSortable: true,
      },
      {
        id: 'editInfo',
        label: '',
        sx: {
          minWidth: '3rem',
          position: 'sticky',
          right: '3rem',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 1,
            boxShadow: '-8px 0px 16px 0px #00000029',
            opacity: 'var(--sticky-actions-shadow, 0)',
            pointerEvents: 'none',
            transition: 'opacity 120ms ease',
          },
        },
      },
      {
        id: 'options',
        label: '',
        sx: {
          position: 'sticky',
          right: 0,
          minWidth: '3rem',
        },
      },
    ],
    [t],
  );

  const rows: Row[] = useMemo(
    () =>
      admins?.map((admin, index) => ({
        id: admin.id,
        cells: [
          {
            value: admin.lastName,
          },
          {
            value: admin.firstName,
          },
          {
            value: admin.email,
          },
          {
            value: getRoleTranslation(admin.role),
            sx: {
              whiteSpace: 'nowrap',
            },
          },
          {
            value: admin.company?.name,
            sx: {
              whiteSpace: 'nowrap',
            },
          },
          {
            value: (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  padding: '0.688rem 1rem',
                }}
              >
                {isUserAllowed(routePermissions.adminsCreate) &&
                admin.allowedOperations.includes(AllowedAdminOperations.CAN_EDIT) ? (
                  <ClickableIcon onClick={() => {}} sx={{ flexShrink: 0, width: '100%' }}>
                    <Link to={`/admins/edit/${admin.id}`}>
                      <PencilIcon />
                    </Link>
                  </ClickableIcon>
                ) : null}
              </Box>
            ),
            sx: {
              position: 'sticky',
              right: '3rem',
              p: 0,
              backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: 1,
                boxShadow: '-8px 0px 16px 0px #00000029',
                opacity: 'var(--sticky-actions-shadow, 0)',
                pointerEvents: 'none',
                transition: 'opacity 120ms ease',
              },
            },
          },
          {
            value: <AdminRowOptions admin={admin} deleteAdmin={deleteAdmin} />,
            sx: {
              placeItems: 'center',
              position: 'sticky',
              right: 0,
              p: 0,
              width: '3rem',
              backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
            },
          },
        ],
      })),
    [t, admins],
  );

  return { headers, rows, totalAdmins, isLoading };
}

export default useAdminsTable;
